export const CRITICAL_SEVERITY = 80;
export const HIGH_SEVERITY = 60;
export const COOLDOWN_CRITICAL_MS = 5 * 60_000;
export const COOLDOWN_DEFAULT_MS = 30 * 60_000;

/** Browser-safe SHA-256 → first 16 hex chars. */
export async function computeAlertKey(signal: {
  id: string;
  source: string;
  summary?: string | null;
  headline?: string | null;
}): Promise<string> {
  // IntelSignal carries `summary`; some upstream wire shapes (AlertEnvelope
  // future-evolutions) may carry `headline`. We prefer the latter when both
  // are present so an editorially-tightened headline doesn't drift from the
  // dedup key as the summary text changes.
  const text = (signal.headline?.trim() || signal.summary?.trim() || '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
  const material = `${text}|${signal.source}|${signal.id}`;
  const bytes = new TextEncoder().encode(material);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hex.slice(0, 16);
}

export interface SeenEntry {
  /** ms-epoch of the most recent display. */
  lastSeenAt: number;
  /** Severity of the most recent display — used to pick the cooldown window. */
  severity: number;
}

export type RecentlySeenMap = Map<string, SeenEntry>;

export function cooldownMsFor(severity: number): number {
  return severity >= CRITICAL_SEVERITY ? COOLDOWN_CRITICAL_MS : COOLDOWN_DEFAULT_MS;
}

/**
 * Decide whether `signal` should display now. Side-effects: prunes expired
 * entries from `recentlySeen`, and on a pass writes the new sighting in.
 *
 * Returns:
 *   - { display: true, key } — chip should render, chime may consider firing
 *   - { display: false, key, reason } — drop silently (caller emits nothing)
 *
 * Pure aside from the map mutation (intentional — the caller owns the map).
 */
export function shouldDisplay(
  signal: { severity: number },
  key: string,
  recentlySeen: RecentlySeenMap,
  now: number,
): { display: true; key: string } | { display: false; key: string; reason: 'cooldown' } {
  // Prune expired in O(n). The longest cooldown is COOLDOWN_DEFAULT_MS, so
  // anything older than that is unconditionally past every window.
  for (const [k, entry] of recentlySeen) {
    if (now - entry.lastSeenAt > COOLDOWN_DEFAULT_MS) recentlySeen.delete(k);
  }

  const existing = recentlySeen.get(key);
  if (existing) {
    const window = cooldownMsFor(existing.severity);
    if (now - existing.lastSeenAt < window) {
      return { display: false, key, reason: 'cooldown' };
    }
  }
  recentlySeen.set(key, { lastSeenAt: now, severity: signal.severity });
  return { display: true, key };
}

/** Classifications that suppress chimes by default. Case-insensitive match. */
export const CLASSIFIED_TIERS = new Set(['CUI', 'RESTRICTED']);

export function isClassified(classification: string | undefined | null): boolean {
  if (!classification) return false;
  return CLASSIFIED_TIERS.has(classification.trim().toUpperCase());
}
